import { useLocation } from 'react-router-dom';
import { type BreadcrumbNode, useBreadcrumbTail } from '@vivicta/ui';

import { ALL_NAV_ENTRIES } from './navEntries';

export type { BreadcrumbNode };

/** Segments spelled out in caps rather than title case. */
const ACRONYMS = new Set(['adr', 'adrs', 'api', 'apis', 'ci', 'cd', 'pr', 'ui', 'ux']);

const humanizeSegment = (segment: string) =>
  decodeURIComponent(segment)
    .replaceAll('-', ' ')
    .replaceAll('_', ' ')
    .split(' ')
    .filter(Boolean)
    .map(word =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');

const NAV_LABEL_BY_PATH = new Map(
  ALL_NAV_ENTRIES.map(entry => [entry.to, entry.title]),
);

/**
 * Pure: builds the crumbs the URL alone can produce. A route registered in
 * `navEntries.tsx` gets its real title; every other segment is humanized from
 * the raw path (kebab/snake case -> Title Case), so every route resolves to
 * at least one level even if it's not a known page.
 */
export const buildStaticCrumbs = (pathname: string): BreadcrumbNode[] => {
  if (pathname === '/') {
    return [{ label: NAV_LABEL_BY_PATH.get('/') ?? 'Home' }];
  }

  const segments = pathname.split('/').filter(Boolean);
  let accumulated = '';

  return segments.map(segment => {
    accumulated += `/${segment}`;
    const label = NAV_LABEL_BY_PATH.get(accumulated) ?? humanizeSegment(segment);
    return { label, to: accumulated };
  });
};

/**
 * Combines the URL-derived crumbs with whatever tail a routed page has
 * contributed via `useSetBreadcrumbTail` (re-exported from `@vivicta/ui`,
 * since plugins may not depend on this app package).
 */
export const useBreadcrumbs = (): BreadcrumbNode[] => {
  const { pathname } = useLocation();
  const tail = useBreadcrumbTail();
  return [...buildStaticCrumbs(pathname), ...tail];
};


